import type { TypedSseEnvelope } from "@pushback-and-chaos/contracts";
import { ProblemJsonError, type PushbackClient } from "@pushback-and-chaos/client";

/**
 * Everything section 4 of the build brief asks for, in one place, because
 * failure legibility only works if there is exactly one thing that knows
 * whether the session is healthy.
 *
 * The backend's streaming decision, confirmed by reading
 * apps/api/src/routes/turns.ts and sse/stream-reply.ts: **buffer-per-turn**.
 * `streamEnvelopes()` writes an already-complete envelope list and ends the
 * response, so the whole turn — including every NARRATION_DELTA — arrives in
 * one burst after the orchestrator finishes. Nothing is ungated by the time
 * it reaches us. This client therefore does NOT type text out
 * sentence-by-sentence as it arrives (there is nothing to reveal
 * progressively); it reveals the turn's narration as one settled block. Any
 * "typing" animation here would be a lie about where the latency is.
 */

export const TURN_TIMEOUT_MS = 45_000;

export type ConnectionState = "live" | "reconnecting" | "lost";

export interface TurnFailure {
  readonly kind: "timeout" | "moved-on" | "network" | "server";
  /** Learner-facing, plain. Never a status code. */
  readonly message: string;
  /** True when an automatic refetch should follow (409/412). */
  readonly refetch: boolean;
}

/**
 * Both channels — the POST /turns response and the GET /stream tail — carry
 * the SAME envelopes with the SAME ids (routes/turns.ts emits to the bus and
 * streams the reply), and several envelopes legitimately share one id (a
 * NARRATION_EMITTED event becomes N deltas plus an end, all at that event's
 * seq). Deduping on id alone would drop real content; deduping on
 * (id, type, ordinal-within-that-pair) is exact, because both channels emit
 * in identical order.
 */
export class EnvelopeLedger {
  private readonly seen = new Map<string, number>();
  private readonly applied = new Set<string>();

  /** Returns true if this envelope has not been applied before. */
  admit(envelope: TypedSseEnvelope): boolean {
    const pair = `${envelope.id}|${envelope.type}`;
    const ordinal = this.seen.get(pair) ?? 0;
    this.seen.set(pair, ordinal + 1);
    const key = `${pair}|${ordinal}`;
    if (this.applied.has(key)) return false;
    this.applied.add(key);
    return true;
  }

  /**
   * A resume replays from `lastEventId`, so the per-pair ordinal counters
   * must restart for the ids being replayed or the second delivery is
   * counted as new. Called before draining a resumed stream.
   */
  rewind(): void {
    this.seen.clear();
  }
}

function isProblem(error: unknown): error is ProblemJsonError {
  return error instanceof ProblemJsonError;
}

export function classifyError(error: unknown): TurnFailure {
  if (isProblem(error)) {
    // 409 LOCK_CONTENTION and 412 STATE_VERSION_MISMATCH both mean the same
    // thing to a learner: something else advanced this session. Never show
    // the number.
    if (error.status === 409 || error.status === 412) {
      return { kind: "moved-on", message: "the session moved on, reloading", refetch: true };
    }
    if (error.status === 404) {
      return { kind: "server", message: "That session is no longer here.", refetch: false };
    }
    if (error.status === 429) {
      return { kind: "server", message: "Too many turns too quickly. Give it a moment, then retry.", refetch: false };
    }
    return { kind: "server", message: "The engagement stalled on its way back. Nothing was lost — you can retry this turn.", refetch: false };
  }
  return { kind: "network", message: "The connection dropped. Nothing was lost — you can retry this turn.", refetch: false };
}

export interface RunTurnArgs {
  readonly client: PushbackClient;
  readonly sessionId: string;
  readonly learnerText: string;
  /**
   * MUST be reused verbatim on retry. apps/api stores the response against
   * this key and replays it rather than rolling again, so a retry after an
   * ambiguous failure cannot double-roll. Generated once per learner
   * submission, not once per attempt.
   */
  readonly idempotencyKey: string;
  readonly onEnvelope: (envelope: TypedSseEnvelope) => void;
  readonly timeoutMs?: number;
}

export interface RunTurnResult {
  readonly ok: boolean;
  readonly failure?: TurnFailure;
  readonly envelopeCount: number;
}

/**
 * One turn, with a hard client-side deadline. Because apps/api buffers the
 * whole turn, "no envelope yet" is the normal state for the entire model
 * pipeline — so the deadline is on the turn, not on the gap between
 * envelopes, and it is the only thing standing between a wedged orchestrator
 * and a spinner that never stops.
 */
export async function runTurn(args: RunTurnArgs): Promise<RunTurnResult> {
  const deadline = Date.now() + (args.timeoutMs ?? TURN_TIMEOUT_MS);
  let count = 0;

  const iterator = args.client.sendTurn(args.sessionId, args.learnerText, { idempotencyKey: args.idempotencyKey })[Symbol.asyncIterator]();

  try {
    while (true) {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        return {
          ok: false,
          envelopeCount: count,
          failure: {
            kind: "timeout",
            message: "This turn has been running for longer than it should. Nothing was lost — you can retry it.",
            refetch: true,
          },
        };
      }

      const step = await Promise.race([
        iterator.next(),
        new Promise<"timeout">((resolve) => setTimeout(() => resolve("timeout"), remaining)),
      ]);

      if (step === "timeout") {
        return {
          ok: false,
          envelopeCount: count,
          failure: {
            kind: "timeout",
            message: "This turn has been running for longer than it should. Nothing was lost — you can retry it.",
            refetch: true,
          },
        };
      }
      if (step.done === true) return { ok: true, envelopeCount: count };
      count += 1;
      args.onEnvelope(step.value);
    }
  } catch (error) {
    return { ok: false, envelopeCount: count, failure: classifyError(error) };
  }
}
